import get from 'lodash/get';
import uniq from 'lodash/uniq';
import size from 'lodash/size';
import createSuccessPromiseThunk from "~/rx/lib/createSuccessPromiseThunk";
import createModelApi from "~/backend/modelApi";
import {
    getItemId, getModelName, getPrimaryKeyAttr, getSubviews,  getModelTree, hasForeginFieldRelation,
    getActiveRelationshipField, 
} from "~/stateGetter";
import identify from "~/identify";
import {modify} from "mythil/object";

import {
    getSaveValues,
    presentPrimaryKey,
} from "~/statePresenter";
import {pushClose, pushCurrentModelPath, smartGoto} from "~/actions/navigation_actions";
import invalidateLoadedModels from "~/reducers/helpers/invalidateLoadedModels";
import {prepareItemData} from "~/data/dataPreparer";

import authenticatedRequest from "~/backend/authenticatedRequest";
import addRelationshipThunk from './helpers/addRelationshipThunk';
import {modelActionsCreator} from "~/actions/model_actions";


const itemSave = createSuccessPromiseThunk({
    func: async ({modelId, getState, dispatch, getConfig, gridName}) => {
        let state = getState(),
            modelApi = createModelApi(
                getConfig().baseUrl,
                getModelName(state, modelId),
                (foo) => authenticatedRequest(foo, dispatch)
            ),
            itemId = getItemId(state, modelId),
            saveValues = getSaveValues(state, modelId, gridName),
            saveResult = await modelApi.saveItem(itemId, saveValues);

        let subviews = getSubviews(state, modelId),
            {hasForeignFieldRelation} = getModelTree(state).getModel(modelId);

        if(!saveResult.success) { return saveResult; }
        if(saveResult.redirect) {
            dispatch(smartGoto(saveResult.redirect, {noConfirm: true,}, { replace: itemId === identify.NEW_ITEM, replaceIfInternal: true }));
            return saveResult;
        }

        let prevItemId = itemId;

        itemId = presentPrimaryKey({
            values: saveResult.values,
            primaryKeyAttr: getPrimaryKeyAttr(state, modelId),
            defaultKey: itemId
        });

        saveResult.itemId = `${itemId}`;
        saveResult.prevItemId = prevItemId;

        if(itemId !== identify.NEW_ITEM && size(subviews) > 0) {
            // We decided to directly navigate to edit mode always
            // let mode = prevItemId === identify.NEW_ITEM ? 'edit' : 'view';

            dispatch(pushCurrentModelPath(`/${itemId}`, {includeCurrent: true, replace: true }, {persistQuery: true, noConfirm: true}))
        } else if (!hasForeignFieldRelation) {
            dispatch(pushClose({noConfirm: true}));
        }


        return prepareItemData(saveResult);
    },
    successHandler: ({defaultSuccessHandler, value, dispatch, customParams, modelId, ...arg}) => {
        defaultSuccessHandler({value, dispatch, customParams, modelId, ...arg});
        if (!get(value, 'success')) return true;

        dispatch(addRelationshipThunk({ ...customParams, modelId, value, }));
        dispatch(modelActionsCreator(modelId).resetItemValue());
        dispatch(modelActionsCreator(modelId).resetRelationshipField());

        return true;
    },
    name: 'saveItem',
});

let execute = itemSave.execute;
itemSave.execute = (modelId, gridName) => execute({modelId, gridName}, {modelId});

let reduce = itemSave.reduce;
itemSave.reduce = (model, action, state) => {
    model = modify(model, 'saveResult', (saveResult) => {
        switch(action.type) {
            case itemSave.types.SUCCESS:
                return {
                    ...saveResult,
                    status: identify.STATUS_SUCCESS,
                    finished_at: Date.now(),
                    value: {
                        messages: action.value.messages
                    }
                };
            // TODO
            default:
                return reduce(saveResult, action);
        }
    });


    return model;
};

itemSave.reduceState = (state, {type, modelId}) => {
    switch(type) {
        case itemSave.types.SUCCESS: {
            const tree = getModelTree(state);
            const parentModelId = tree.getModel(modelId).parentModelId;

            const activeRelationshipField = parentModelId && getActiveRelationshipField(state, parentModelId);
            modelId = !hasForeginFieldRelation(state, modelId)
                ? parentModelId || modelId
                : getModelTree(state)
                    .getChildren(modelId)
                    .filter((model) => model.modelName === activeRelationshipField.model)
                    .map(model => model.modelId);

            return invalidateLoadedModels(state, {
                include: uniq([].concat(modelId)),
            });
        }
        default:
            return state;
    }
};

const reduceSaveItem = itemSave.reduce,
    SAVE_ITEM_TYPES = itemSave.types,
    saveItem = itemSave.execute,
    reduceItemSaveState = itemSave.reduceState;

export {
    reduceSaveItem,
    reduceItemSaveState,
    SAVE_ITEM_TYPES,
    saveItem,
}